import { t } from '@/locales'
import type { TStatusChipVariant } from '@team-milva/lab-components'
import type { IOrderPriorityConfig, IOrderPriorityTier } from '@team-milva/lab-models/labAccount'

// Whole calendar days between two instants, midnight to midnight in local time.
// Built from UTC-normalised Y/M/D so a DST change inside the span can't turn a
// 24h-multiple into 23h/25h and skew the rounding.
function calendarDaysBetween(fromMs: number, toMs: number): number {
  const from = new Date(fromMs)
  const to = new Date(toMs)
  const fromDay = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())
  const toDay = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate())
  return Math.abs(Math.round((toDay - fromDay) / 86_400_000))
}

// 24h or more away: a calendar-day count (Fri 23:30 → Wed 14th reads as 12 days,
// the way people count it), not 24h blocks, which read one low when ordering late
// in the day. Under 24h: hours and minutes, unchanged. A gap of 24h or more always
// spans at least one midnight, so the day count is never 0.
export function formatTimeRemaining(deadlineMs: number): string {
  const now = Date.now()
  const ms = deadlineMs - now
  const isNegative = ms < 0
  const absMs = Math.abs(ms)

  let result: string
  if (absMs >= 86_400_000) {
    const days = calendarDaysBetween(now, deadlineMs)
    result = days === 1 ? t('W: 1 dag') : t('W: {days} dage', { days })
  } else {
    const hours = Math.floor(absMs / 3_600_000)
    const minutes = Math.floor((absMs % 3_600_000) / 60_000)
    result = hours > 0 ? `${hours}t ${minutes}m` : `${minutes}m`
  }

  return isNegative ? `-${result}` : result
}

// Given the lab's orderPriorityConfig and how much time is left (ms, may be
// negative when overdue) until a deadline, returns the matching tier — or
// null when coloring is disabled/unset. Kept standalone (not folded into
// getTimeRemainingChip()) so any future "time left" surface (a filter, a
// dashboard widget, a different list view) can reuse the exact same
// matching logic without duplicating it.
//
// This governs time-left coloring ONLY — never order status. Order.getStatusChip()
// and every other lifecycle-status chip in the app must stay untouched by this.
//
// Tiers are stored pre-sorted ascending by thresholdHours with the catch-all
// (thresholdHours: null) tier always last (enforced by both the settings UI
// and the lab-cloud Zod schema), so a plain first-match scan is sufficient —
// no re-sorting needed here.
//
// Overdue orders (remainingMs < 0) are not a distinct state: a negative hour
// count is smaller than every configured threshold, so it naturally falls
// into the first (most urgent) tier — no special-casing required, and a lab
// can always add a tighter tier if they want overdue orders to stand out
// further.
export function getMatchingOrderPriorityTier(
  orderPriorityConfig: IOrderPriorityConfig | undefined,
  remainingMs: number
): IOrderPriorityTier | null {
  if (!orderPriorityConfig?.enabled) return null

  const remainingHours = remainingMs / 3_600_000

  for (const tier of orderPriorityConfig.tiers) {
    if (tier.thresholdHours === null || remainingHours < tier.thresholdHours) {
      return tier
    }
  }

  return null
}

export function getOrderPriorityVariant(
  orderPriorityConfig: IOrderPriorityConfig | undefined,
  remainingMs: number
): TStatusChipVariant {
  return getMatchingOrderPriorityTier(orderPriorityConfig, remainingMs)?.variant ?? 'neutral'
}

export function getTimeRemainingChip(
  deadlineMs: number,
  orderPriorityConfig?: IOrderPriorityConfig
): {
  variant: TStatusChipVariant
  label: string
} {
  return {
    variant: getOrderPriorityVariant(orderPriorityConfig, deadlineMs - Date.now()),
    label: formatTimeRemaining(deadlineMs),
  }
}

/**
 * The moment a scheduled delivery will be sent, or null when the delivery is not scheduled (or the
 * moment is not stored). Read off the order's own delivery group — the shared delivery list does
 * not carry it.
 */
export function getScheduledSendMs(
  delivery: { labReportStatus: string; tatGroupID: string },
  tatGroups: Record<
    string,
    { timestamps?: { labReportScheduledForAt?: number | null } }
  >
): number | null {
  if (delivery.labReportStatus !== 'scheduled') return null
  return tatGroups[delivery.tatGroupID]?.timestamps?.labReportScheduledForAt ?? null
}

/**
 * The chip for an order's next delivery on the orders list. Normally the time left to the
 * delivery's deadline. When that delivery is scheduled it counts down to the send instead and says
 * so ("3 dage – planlagt") — a scheduled delivery is already taken care of, so the label is the
 * send time, but the colour still comes from the deadline, exactly as it does for any other order.
 */
export function getNextDeliveryChip(
  deadlineMs: number,
  scheduledSendMs: number | null,
  orderPriorityConfig?: IOrderPriorityConfig
): {
  variant: TStatusChipVariant
  label: string
} {
  const chip = getTimeRemainingChip(deadlineMs, orderPriorityConfig)
  if (scheduledSendMs === null) return chip

  // The send moment has passed but the job has not picked the delivery up yet — a count of
  // negative minutes would read as an error.
  const time =
    scheduledSendMs > Date.now() ? formatTimeRemaining(scheduledSendMs) : t('W: Afsendes nu')
  return { variant: chip.variant, label: t('W: {time} – planlagt', { time }) }
}

// vue-i18n's plural pipe syntax always bakes the count into the string, so a
// plain W: ternary is used instead — this returns just the unit word.
export function hourUnitLabel(hours: number | null): string {
  return hours === 1 ? t('W: time') : t('W: timer')
}

function getHighestThresholdHours(tiers: IOrderPriorityTier[]): number | null {
  const numeric = tiers
    .filter((tier) => tier.thresholdHours !== null)
    .map((tier) => tier.thresholdHours as number)
  return numeric.length > 0 ? Math.max(...numeric) : null
}

// The range text for one tier within its lab's full tier list — the same
// text shown on the settings page's live chip preview and used as an
// order-list filter option's label, generated straight from the thresholds
// so it can never drift out of sync with them. Tiers past the first show a
// full range using the previous tier's threshold as the lower bound (e.g.
// "8–24 hours"), not the ambiguous-looking "< 24 hours".
export function getOrderPriorityTierRangeLabel(
  tiers: IOrderPriorityTier[],
  tier: IOrderPriorityTier
): string {
  if (tier.thresholdHours !== null) {
    const index = tiers.findIndex((x) => x.id === tier.id)
    const previousTier = index > 0 ? tiers[index - 1] : null
    if (previousTier && previousTier.thresholdHours !== null) {
      return `${previousTier.thresholdHours}–${tier.thresholdHours} ${hourUnitLabel(tier.thresholdHours)}`
    }
    return `< ${tier.thresholdHours} ${hourUnitLabel(tier.thresholdHours)}`
  }

  const highestThresholdHours = getHighestThresholdHours(tiers)
  return highestThresholdHours !== null
    ? `> ${highestThresholdHours} ${hourUnitLabel(highestThresholdHours)}`
    : t('W: Alle')
}
